import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';
import { LoadingIcon } from './Loading';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none  [&_svg]:pointer-events-none cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-secondary hover:text-primary disabled:bg-[#B4B4B4] disabled:text-white',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 disabled:bg-[#B4B4B4] disabled:text-white',
        outline:
          'border border-primary text-primary bg-background shadow-xs hover:bg-secondary hover:text-primary disabled:border-[#A0A0A0] disabled:text-[#A0A0A0]',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-[#1a1a1a] border border-primary hover:bg-transparent disabled:bg-[#B4B4B4] disabled:text-white',
        ghost: 'hover:brightness-50 disabled:opacity-50',
        link: 'text-secondary underline-offset-3 hover:underline font-bold decoration-2',
      },
      size: {
        default: 'h-9 px-4 py-[6px]',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9 [&_svg]:size-4 [&_svg]:shrink-0',
        custom: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      disabled = false,
      asChild = false,
      loading = false,
      className,
      variant,
      size,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {loading ? (
          <>
            <LoadingIcon />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
